/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { HashRouter, Route, Routes } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import { MsalProvider } from '@azure/msal-react';

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

const Page404 = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'));
import ForgotPassword from './views/pages/ForgotPassword/ForgotPassword';
import Login from './views/pages/login/Login';
import DecryptionTool from './components/DecryptionTool/DecryptionTool.jsx';
import RMS from './components/RMS/RMS.js';
import './scss/style.scss';
import Submit from './views/pages/submit/Submit';
import { ToastContainer } from 'react-toastify';

class App extends Component {
  componentDidMount() {
    // Remove /app from URL if present
    if (window.location.pathname.includes('/app')) {
      const newPathname = window.location.pathname.replace(/\/app\/?/, '/');
      const newUrl =
        window.location.origin + newPathname + window.location.hash + window.location.search;
      window.location.replace(newUrl);
      return;
    }

    if (window.location?.href?.includes('upload')) {
      document.title = 'Autire - Upload Documents';
    } else {
      document.title = 'Home';
    }
  }
  render() {
    // If MSAL instance is not available, show a warning and fallback to basic routing
    if (!this.props.msalInstance) {
      return (
        <div>
          <ToastContainer />
          <HashRouter>
            <React.Suspense fallback={loading}>
              <div className="alert alert-warning" style={{ margin: '20px', padding: '20px' }}>
                <h4>Authentication Service Unavailable</h4>
                <p>Microsoft Authentication Library (MSAL) is not available. This may be due to:</p>
                <ul>
                  <li>Running on HTTP instead of HTTPS</li>
                  <li>Browser security restrictions</li>
                  <li>Missing crypto API support</li>
                </ul>
                <p>Please ensure you are running on HTTPS or localhost for full functionality.</p>
              </div>
              <Routes>
                <Route exact path="/login" name="Login Page" element={<Login />} />
                <Route exact path="/404" name="Page 404" element={<Page404 />} />
                <Route exact path="/500" name="Page 500" element={<Page500 />} />
                <Route
                  path="/*"
                  name="Home"
                  element={<div>Authentication required but not available</div>}
                />
              </Routes>
            </React.Suspense>
          </HashRouter>
        </div>
      );
    }

    return (
      <MsalProvider instance={this.props.msalInstance}>
        <ToastContainer />
        <HashRouter>
          <React.Suspense fallback={loading}>
            <Routes>
              <Route
                exact
                path="/forgot-password"
                name="Forgot Password"
                element={<ForgotPassword />}
              />
              <Route exact path="/login" name="Login Page" element={<Login />} />
              <Route exact path="/404" name="Page 404" element={<Page404 />} />
              <Route exact path="/500" name="Page 500" element={<Page500 />} />
              <Route exact path="/upload" name="Upload" element={<Submit />} />
              <Route
                exact
                path="/decryption-tool"
                name="Decryption Tool"
                element={<DecryptionTool />}
              />
              <Route exact path="/rms" name="RMS" element={<RMS />} />
              <Route path="/*" name="Home" element={<PrivateRoute />} />
            </Routes>
          </React.Suspense>
        </HashRouter>
      </MsalProvider>
    );
  }
}

App.propTypes = {
  msalInstance: PropTypes.object,
};

export default App;
